// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * AssetStatusLegend component for displaying asset status color indicators.
 * Shows what each colored dot means in the inventory overview.
 */
import { AssetFunctionalStatus } from '@/constants/asset-functional-status';
import { AssetStatusLabels } from '@/inventory/components/overview/types';
import { Box, Stack, Typography } from '@mui/material';

const statusConfig: Record<AssetFunctionalStatus, { color: string }> = {
  FUNCTIONAL: {
    color: '#4CAF50', // Green
  },
  NEEDS_REVIEW: {
    color: '#FFC107', // Orange/Amber
  },
  NON_FUNCTIONAL: {
    color: '#F44336', // Red
  },
  SCRAPPED: {
    color: '#9E9E9E', // Grey
  },
};

const statusOrder: AssetFunctionalStatus[] = [
  'FUNCTIONAL',
  'NEEDS_REVIEW',
  'NON_FUNCTIONAL',
  'SCRAPPED',
];

interface AssetStatusLegendProps {
  /** Whether to show the scrapped status in the legend */
  showScrappedAssets?: boolean;
}

/**
 * AssetStatusLegend component
 */
export function AssetStatusLegend(props: AssetStatusLegendProps) {
  const { showScrappedAssets = false } = props;

  const statusesToShow = statusOrder.filter(
    (status) => showScrappedAssets || status !== 'SCRAPPED'
  );

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        mx: 0,
        my: 1,
      }}
    >
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'grey.300',
          borderRadius: 1,
          py: 1,
          px: 1.5,
          backgroundColor: 'background.paper',
        }}
      >
        <Stack direction="row" spacing={3}>
          {statusesToShow.map((status) => (
            <Stack
              key={status}
              direction="row"
              spacing={0.5}
              alignItems="center"
            >
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  backgroundColor: statusConfig[status].color,
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {AssetStatusLabels[status]}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}
